import {
  appendEquityExtraBenchmarkColumns,
  appendTxnBenchmarkColumns,
  EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
  TXN_BENCHMARK_COLUMN_ORDER,
  withTxnBenchmarkColumns,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  appendHoldingLevelCostColumns,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  EQUITY_ALL_COMMON_COLUMNS,
  EQUITY_MIS_COLUMNS,
  EQUITY_POINT_TO_POINT_REPORT,
  EQUITY_TRANSACTION_COLUMNS,
  EQUITY_UNREALIZED_HOLDING_COLUMNS,
  FieldColumn,
  getColumnsForEquityReportType,
  INVESTOR_COLUMN,
} from "./equityReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const STOCK_SEGMENT = "STOCK";

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
]);

const UNREALIZED_HOLDING_TYPE_COLUMN: FieldColumn = {
  key: "type",
  value: REPORT_GAIN_TYPE_COLUMN_LABEL,
  locked: true,
  minWidth: 132,
};

const REPORT_DAYS_COLUMN: FieldColumn = {
  key: "days",
  value: "Avg. Days",
  minWidth: 88,
};

const REPORT_COST_COLUMN_KEYS = [
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const STOCK_ALL_REPORT_AFTER_ADJ_COLUMNS: FieldColumn[] = [
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

const EQUITY_P2P_REPORT_COLUMN_ORDER = [
  "symbol",
  "exchange",
  "transType",
  "units",
  "investedAmount",
  "currentAmount",
  "currentNAV",
  "avgPrice",
  "xirr",
  "cagr",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
  ...TXN_BENCHMARK_COLUMN_ORDER,
  ...EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
] as const;

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [
    ...without.slice(0, insertAt),
    UNREALIZED_CAGR_COLUMN,
    ...without.slice(insertAt),
  ];
};

const stripIsinColumn = (cols: FieldColumn[]): FieldColumn[] =>
  cols.filter((c) => c.key !== "isin");

const withUnrealizedHoldingTypeColumn = (
  cols: FieldColumn[],
  assetClass: string,
): FieldColumn[] => {
  if (!cols.length) return cols;

  let result =
    assetClass === "PRIVATE_STOCK" ? stripIsinColumn(cols) : cols;

  result = result.map((c) =>
    c.key === "type" ? { ...c, value: REPORT_GAIN_TYPE_COLUMN_LABEL } : c,
  );

  if (result.some((c) => c.key === "type")) {
    return withUnrealizedCagrColumn(result);
  }

  const isinIdx = result.findIndex((c) => c.key === "isin");
  const nameIdx = result.findIndex(
    (c) => c.key === "symbol" || c.key === "schemeName",
  );
  const insertAt =
    isinIdx >= 0 ? isinIdx + 1 : nameIdx >= 0 ? nameIdx + 1 : 1;

  return withUnrealizedCagrColumn([
    ...result.slice(0, insertAt),
    UNREALIZED_HOLDING_TYPE_COLUMN,
    ...result.slice(insertAt),
  ]);
};

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName" || c.key === "symbol")) return cols;
  return [
    { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
    ...cols,
  ];
};

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(
      c.key as (typeof REPORT_COST_COLUMN_KEYS)[number],
    ),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [
    ...withoutDays.slice(0, insertAt),
    daysCol,
    ...withoutDays.slice(insertAt),
  ];
};

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const orderColumnsByKeys = (
  columns: FieldColumn[],
  orderKeys: readonly string[],
): FieldColumn[] => {
  const order = new Map(orderKeys.map((key, idx) => [key, idx]));
  return [...columns].sort(
    (a, b) => (order.get(a.key) ?? 999) - (order.get(b.key) ?? 999),
  );
};

const orderTrailingColumnsAfterAdjCost = (
  columns: FieldColumn[],
  trailingColumns: FieldColumn[],
): FieldColumn[] => {
  const trailingKeySet = new Set(trailingColumns.map((c) => c.key));
  const without = columns.filter((c) => !trailingKeySet.has(c.key));
  const trailingCols = trailingColumns.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return existing ?? def;
  });
  const adjIdx = without.findIndex((c) => c.key === "adjustedCostPrice");
  const insertAt = adjIdx >= 0 ? adjIdx + 1 : without.length;
  return [
    ...without.slice(0, insertAt),
    ...trailingCols,
    ...without.slice(insertAt),
  ];
};

const orderStockAllAfterAdjColumns = (columns: FieldColumn[]): FieldColumn[] =>
  orderTrailingColumnsAfterAdjCost(columns, STOCK_ALL_REPORT_AFTER_ADJ_COLUMNS);

const filterAllReportColumns = (columns: FieldColumn[]): FieldColumn[] => {
  const hidden = new Set(["folioNumber", "folioNo"]);
  return columns.filter((c) => !hidden.has(c.key));
};

const appendEquityBenchmarkAndCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildEquityHoldingPreset = (assetClass: string): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(EQUITY_UNREALIZED_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumns(cols, STOCK_SEGMENT);
  cols = appendTxnBenchmarkColumns(cols);
  return appendEquityExtraBenchmarkColumns(cols);
};

const buildEquityHoldingColumns = (assetClass: string): FieldColumn[] =>
  appendEquityBenchmarkAndCostColumns(
    withUnrealizedHoldingTypeColumn(buildEquityHoldingPreset(assetClass), assetClass),
    { placeDaysBeforeStt: true },
  );

const buildEquityRealizedColumns = (assetClass: string): FieldColumn[] => {
  let cols = getColumnsForEquityReportType("Realized");
  if (assetClass === "PRIVATE_STOCK") {
    cols = stripIsinColumn(cols);
  }
  cols = withTxnMetadataAndBenchmarkColumns(cols, STOCK_SEGMENT, {
    includeBenchmarks: true,
  }) as FieldColumn[];
  cols = withUnrealizedCagrColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  return stripMemberNameColumn(cols);
};

const buildEquityAllColumns = (): FieldColumn[] => {
  let cols = filterAllReportColumns(
    appendTxnBenchmarkColumns(
      appendHoldingLevelCostColumns(
        appendAdjustedCostPriceColumn(EQUITY_ALL_COMMON_COLUMNS),
        STOCK_SEGMENT,
      ),
    ),
  );
  cols = ensureReportDaysColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = orderStockAllAfterAdjColumns(cols);
  return appendEquityBenchmarkAndCostColumns(cols, { placeDaysBeforeStt: true });
};

const buildEquityTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    appendEquityExtraBenchmarkColumns(
      withTxnMetadataAndBenchmarkColumns(
        EQUITY_TRANSACTION_COLUMNS,
        STOCK_SEGMENT,
        { includeBenchmarks: true },
      ) as FieldColumn[],
    ),
  );
  cols = withUnrealizedCagrColumn(cols);
  return stripMemberNameColumn(stripDaysColumn(cols));
};

const buildEquityMisColumns = (): FieldColumn[] =>
  stripMemberNameColumn(appendAdjustedCostPriceColumn(EQUITY_MIS_COLUMNS));

const buildEquityTaxColumns = (): FieldColumn[] =>
  stripMemberNameColumn(getColumnsForEquityReportType("Short Term Capital Gain"));

const buildEquityP2pColumns = (): FieldColumn[] => {
  let cols = appendEquityExtraBenchmarkColumns(
    withTxnBenchmarkColumns(getColumnsForEquityReportType(EQUITY_POINT_TO_POINT_REPORT)),
  );
  cols = stripMemberNameColumn(cols);
  return orderColumnsByKeys(cols, EQUITY_P2P_REPORT_COLUMN_ORDER);
};

export const getBaseColumnsForReportType = (
  reportType: string,
  assetClass: string,
): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return buildEquityHoldingColumns(assetClass);
    case "Realized":
      return buildEquityRealizedColumns(assetClass);
    case "All":
      return buildEquityAllColumns();
    case "Transaction":
      return buildEquityTransactionColumns();
    case EQUITY_POINT_TO_POINT_REPORT:
      return buildEquityP2pColumns();
    case "MIS Report":
      return buildEquityMisColumns();
    case "Intraday":
    case "Short Term Capital Gain":
    case "Long Term Capital Gain":
      return buildEquityTaxColumns();
    default:
      return getColumnsForEquityReportType(reportType);
  }
};

export const buildSelectableColumns = (
  reportType: string,
  assetClass: string,
): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType, assetClass),
];

export const buildEffectiveColumns = (
  reportType: string,
  assetClass: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType, assetClass);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (
  reportType: string,
  assetClass: string,
): string[] => {
  const keys = buildSelectableColumns(reportType, assetClass).map((c) => c.key);
  if (reportType !== "Transaction" && reportType !== EQUITY_POINT_TO_POINT_REPORT) {
    return keys;
  }
  const hiddenExtra = new Set<string>(EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER);
  return keys.filter((key) => !hiddenExtra.has(key));
};
